/**
 * AnalyzeContent — parent/orchestrator
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import InputPanel from './analyze/InputPanel';
import ResultsPanel from './analyze/ResultsPanel';
import PageLoader from './PageLoader';
import PageAlert from './PageAlert';

// ── Local quick-prediction heuristic ────────────────────────────────────────
function computePrediction(content) {
  let score = 50;
  const suggestions = [];
  const wc = content.trim().split(/\s+/).length;

  if (wc >= 20 && wc <= 150) { score += 20; suggestions.push('Perfect length for engagement!'); }
  else if (wc < 20) { score -= 10; suggestions.push('Consider making your content longer.'); }
  else { score -= 5; suggestions.push('Long content may lose reader attention.'); }

  if (content.includes('?')) { score += 10; suggestions.push('Questions drive interaction!'); }
  if (/[\u{1F300}-\u{1FAFF}]/u.test(content)) { score += 10; suggestions.push('Emojis boost visual appeal!'); }
  if (/\d/.test(content)) { score += 10; suggestions.push('Numbers add credibility.'); }
  if (/comment|share|thoughts|follow/i.test(content)) { score += 15; suggestions.push('Great call-to-action!'); }

  score = Math.max(0, Math.min(100, score));
  const virality = score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low';
  let best = 'Twitter';
  if (/professional|business|career/i.test(content)) best = 'LinkedIn';
  else if (/photo|image|video|visual/i.test(content)) best = 'Instagram';
  else if (wc > 100) best = 'LinkedIn';

  return { score: Math.round(score), virality, best, suggestions };
}



// ════════════════════════════════════════════════════════════════════════════
const AnalyzeContent = React.memo(({ onAnalyze, loading, analysis, error }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ── Local state ────────────────────────────────────────────────────────
  const [text, setText] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [predLoading, setPredLoading] = useState(false);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!text.trim()) return;

    // fire both calls concurrently
    onAnalyze(text);

    setPredLoading(true);
    await new Promise(r => setTimeout(r, 900));
    setPrediction(computePrediction(text));
    setPredLoading(false);
  }, [text, onAnalyze]);

  // ── Shared card style (passed down so children stay in sync) ──────────
  const card = useMemo(() => ({
    p: { xs: 3, md: 4 },
    borderRadius: 5,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.03)',
  }), [theme, isDark]);

  if (loading && !analysis) {
    return (
      <Box sx={{ py: 4 }}>
        <PageLoader type="grid" count={3} />
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  return (
    <Box>
      {/* ── Standard Page Header ── */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
          Content IQ
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          AI-powered sentiment, engagement, and virality analysis for your content
        </Typography>
      </Box>

      <PageAlert error={error} />

      {/* ── TWO-COLUMN GRID ─────────────────────────────────────────────── */}
      <Grid container spacing={4} alignItems="stretch">

        {/* Left — input */}
        <Grid xs={12} lg={4}>
          <InputPanel
            text={text}
            onTextChange={setText}
            selectedPlatform={selectedPlatform}
            onPlatformChange={setSelectedPlatform}
            onSubmit={handleSubmit}
            loading={loading}
            predLoading={predLoading}
            prediction={prediction}
          />
        </Grid>

        {/* Right — results */}
        <Grid xs={12} lg={8} sx={{ display: 'flex', flexDirection: 'column', minHeight: 600 }}>
          <ResultsPanel analysis={analysis} loading={loading} card={card} />
        </Grid>

      </Grid>
    </Box>
  );
});

export default AnalyzeContent;